import React, { useMemo, useState } from "react";
import { graphql, navigate } from "gatsby";
import { ContactPage } from "schema-dts";

import * as styles from "./contact.module.scss";
import logoFull from "../../assets/images/logo_full.png";
import contactHeroImage from "../../assets/images/contact/hero_contact.jpeg";
import usePageMetadata from "../../hooks/use-page-metadata";
import useUser from "../../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../../apps/utility/routes.utility";
import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";

import MainLayout from "../../layouts/main-layout";
import Link from "../atoms/link";
import ContactForm from "../organisms/contact-form";
import MinimalFooter from "../organisms/minimal-footer";

export interface IContactProps extends IPageBase {
    readonly data: {
        site: ISite;
    };
}

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

const Contact = ({ data, pageContext }: IContactProps) => {
    const { pageTitle, siteMetadata, defaultStructuredData } = usePageMetadata(
        pageContext,
        data.site
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isLoggedIn } = useUser();
    const accountLinkHref = isLoggedIn ? getAbsolutePath("ACCOUNT_DASHBOARD") : "/auth/";
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";

    const structuredData = useMemo(() => {
        const contactPage: ContactPage = {
            "@type": "ContactPage",
            "@id": `${siteMetadata.url}/#website`,
        };
        return {
            "@graph": [...defaultStructuredData["@graph"], contactPage],
        };
    }, [defaultStructuredData]);

    return (
        <MainLayout
            HeaderComponent={null}
            footer={false}
            className={styles.contactPage}
            SEOProps={{
                title: pageTitle,
                structuredData,
            }}
        >
            <nav className={styles.contactNav} aria-label="Główna nawigacja">
                <Link to="/" className={styles.brand}>
                    <img
                        src={logoFull}
                        alt="Fashion Fairy Virtual Stylist"
                        className={styles.brandLogo}
                    />
                </Link>
                <div className={styles.navLinks}>
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                </div>
                <Link to={accountLinkHref} className={styles.loginButton} noPrefetch>
                    {accountLinkLabel}
                </Link>
                <button
                    className={styles.menuToggle}
                    type="button"
                    aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="contact-mobile-menu"
                    onClick={() => setIsMenuOpen(isOpen => !isOpen)}
                >
                    <span />
                    <span />
                    <span />
                </button>
                <div
                    id="contact-mobile-menu"
                    className={`${styles.mobileMenu} ${
                        isMenuOpen ? styles.mobileMenuOpen : ""
                    }`}
                >
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            setIsMenuOpen(false);
                            navigate(accountLinkHref);
                        }}
                    >
                        {accountLinkLabel}
                    </button>
                </div>
            </nav>

            <main className={styles.contactHero}>
                <section className={styles.heroImageWrap}>
                    <img src={contactHeroImage} alt="" />
                    <div className={styles.contactIntro}>
                        <p className={styles.kicker}>Kontakt</p>
                        <h1>Napisz do nas</h1>
                        <p className={styles.lead}>
                            Masz pytanie o stylizacje, współpracę albo działanie Fashion Fairy?
                            Napisz wiadomość, a wrócimy z odpowiedzią możliwie szybko.
                        </p>
                        <div className={styles.contactCard}>
                            <span>Email</span>
                            <a href="mailto:kontakt@fashionfairy.pl">
                                kontakt@fashionfairy.pl
                            </a>
                        </div>
                    </div>
                </section>

                <section className={styles.formCard} aria-label="Formularz kontaktowy">
                    <ContactForm className={styles.contactForm} />
                </section>
            </main>

            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }

        site {
            ...siteFields
        }
    }
`;

export default Contact;
